/**
 * FERP Portal - Entity Preview Card
 *
 * Hover card showing entity details without navigation.
 * Lazy-loads full entity data on hover (500ms delay).
 *
 * FERP Principles:
 * - Repository pattern: GraphQL query via useQuery
 * - Dumb frontend: Backend defines what fields to show
 * - Data-driven: Preview fields from backend
 */

import { Card, Text, Stack, Badge, Group, Loader, ThemeIcon } from '@mantine/core';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { IconCalendar, IconUser, IconAlertCircle } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';

const GET_ENTITY_PREVIEW = gql`
  query GetEntityPreview($entityType: String!, $id: String!) {
    entityPreview(entityType: $entityType, id: $id) {
      id
      displayValue
      status
      createdAt
      updatedAt
      createdBy
      isLifeCritical
      metadata
    }
  }
`;

export interface EntityPreviewCardProps {
  entityType: string;
  entityId: string;
}

export function EntityPreviewCard({ entityType, entityId }: EntityPreviewCardProps) {
  const { data, loading, error } = useQuery<Record<string, any>>(GET_ENTITY_PREVIEW, {
    variables: { entityType, id: entityId },
    // Lazy load - only fetch when hover card opens
    fetchPolicy: 'cache-first',
  });

  if (loading) {
    return (
      <Card padding="md" radius="md" withBorder>
        <Group justify="center">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading preview...
          </Text>
        </Group>
      </Card>
    );
  }

  if (error || !data?.entityPreview) {
    return (
      <Card padding="md" radius="md" withBorder>
        <Group>
          <ThemeIcon color="red" variant="light">
            <IconAlertCircle size={16} />
          </ThemeIcon>
          <Text size="sm" c="red">
            Failed to load preview
          </Text>
        </Group>
      </Card>
    );
  }

  const preview = data.entityPreview;

  return (
    <Card shadow="md" padding="md" radius="md" withBorder>
      <Stack gap="xs">
        {/* Header: Display name + status */}
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} size="sm" lineClamp={1}>
            {preview.displayValue}
          </Text>
          {preview.status && (
            <Badge size="sm" variant="light">
              {preview.status}
            </Badge>
          )}
        </Group>

        {/* Life-critical indicator */}
        {preview.isLifeCritical && (
          <Badge color="red" size="xs" variant="filled">
            LIFE-CRITICAL
          </Badge>
        )}

        {/* Created info */}
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="gray">
            <IconCalendar size={12} />
          </ThemeIcon>
          <Text size="xs" c="dimmed">
            Created{' '}
            {preview.createdAt
              ? formatDistanceToNow(new Date(preview.createdAt), { addSuffix: true })
              : 'unknown'}
          </Text>
        </Group>

        {/* Created by */}
        {preview.createdBy && (
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="gray">
              <IconUser size={12} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">
              by {preview.createdBy}
            </Text>
          </Group>
        )}

        {/* Last updated */}
        {preview.updatedAt && preview.updatedAt !== preview.createdAt && (
          <Text size="xs" c="dimmed">
            Updated {formatDistanceToNow(new Date(preview.updatedAt), { addSuffix: true })}
          </Text>
        )}

        {/* Metadata preview (optional) */}
        {preview.metadata && Object.keys(preview.metadata).length > 0 && (
          <Text size="xs" c="dimmed" fs="italic">
            +{Object.keys(preview.metadata).length} metadata fields
          </Text>
        )}
      </Stack>
    </Card>
  );
}

export default EntityPreviewCard;
